import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { CheckCircle2, AlertCircle, ArrowRight, Package, Truck, Factory, Wrench, Calendar, Upload, Anchor } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
const whatWeBuy = [{
  id: "item-1",
  title: "Maritime & Ocean Freight",
  icon: <Truck className="size-5 text-primary" />,
  content: "Connecting vessels with independent maritime suppliers. We facilitate RFQ coordination for freight services but do not handle cargo transport or delivery guarantees."
}, {
  id: "item-2",
  title: "Heavy Lift & Project Cargo",
  icon: <Wrench className="size-5 text-primary" />,
  content: "Facilitating connections for specialized industrial machinery sourcing. We coordinate RFQs between operators and suppliers; Venchandlers Nexus does not transport or guarantee delivery of assets."
}, {
  id: "item-3",
  title: "Intermodal & Rail Networks",
  icon: <Factory className="size-5 text-primary" />,
  content: "Bridging the gap between inland hubs and maritime suppliers. Our platform coordinates RFQs for intermodal needs without assuming responsibility for physical transport or delivery."
}, {
  id: "item-4",
  title: "Customs & Global Compliance",
  icon: <Package className="size-5 text-primary" />,
  content: "Connecting operators with compliance specialists and suppliers. We facilitate RFQ coordination for regulatory needs; we do not provide legal guarantees or manage physical cargo movement."
}];
const supplyOptions = ["Provisions (Food & Water)", "Fuel (Bunker)", "Spare Parts", "Cleaning & Hygiene", "Safety Equipment", "Other"];
export default function AcquisitionInquiry() {
  return <section data-section-id="31903" id="request-supply-quote" className="relative py-24 bg-[#0A192F] text-slate-100 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[url(/img/maritime_brokerage_background.png)] bg-cover bg-center grayscale opacity-20 mix-blend-overlay" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
      </div>

      <div className="container relative mx-auto px-4 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          
          <div className="space-y-10">
            <div className="space-y-6">
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-white font-serif uppercase">
                Request Supply <span className="text-primary">Quote</span>
              </h2>
              <div className="space-y-2">
                <p className="text-lg text-slate-300 leading-relaxed max-w-xl font-sans">
                  Venchandlers Nexus connects vessels with independent suppliers, facilitating RFQ coordination for maritime needs. We do not transport cargo or guarantee delivery.
                </p>
                <div className="pt-4 font-mono text-[10px] tracking-widest text-slate-500 uppercase">
                  COORD: 41.4993° N, 81.6944° W | OPS: 0800 - 1800 EST
                </div>
              </div>
            </div>

            <div className="bg-[#0D1F3D]/80 backdrop-blur-sm border border-slate-800 rounded-none shadow-none overflow-hidden">
              <div className="p-6 border-b border-slate-800 bg-slate-900/40">
                <h3 className="text-sm font-mono uppercase tracking-widest flex items-center gap-2 text-slate-200">
                  <CheckCircle2 className="size-4 text-primary" />
                  Service Capabilities
                </h3>
              </div>
              <Accordion type="single" collapsible className="w-full">
                {whatWeBuy.map((item, i) => <AccordionItem key={item.id} value={item.id} className="px-6 border-b-slate-800/50" data-index={i}>
                    <AccordionTrigger className="hover:text-primary transition-colors py-5 font-serif uppercase tracking-tight text-slate-200">
                      <span className="flex items-center gap-3 text-left">
                        {item.icon}
                        {item.title}
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-400 leading-relaxed pb-6 pl-8 font-sans">
                      {item.content}
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>
            </div>

            <div className="hidden lg:block p-6 bg-primary/5 border border-primary/10 rounded-none">
              <h4 className="font-serif font-bold text-white uppercase tracking-tight mb-2 flex items-center gap-2">
                <AlertCircle className="size-5 text-primary" />
                Urgent Logistics Support
              </h4>
              <p className="text-sm text-slate-400 mb-4 font-sans">
                For urgent RFQ assistance or complex supply coordination needs, contact our operations desk immediately.
              </p>
              <Link to="tel:440.942.3003" className="text-lg font-bold text-primary hover:underline font-mono">
                440.942.3003
              </Link>
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-1 bg-gradient-to-b from-primary/10 to-transparent rounded-none blur-sm opacity-50" />
            <div className="relative bg-[#0D1F3D] border border-slate-800 rounded-none shadow-2xl p-6 md:p-8">
              <div className="mb-8 border-l-2 border-primary pl-4">
                <h3 className="text-2xl font-serif font-bold text-white uppercase tracking-tight mb-2">Request Supply Quote</h3>
                <p className="text-slate-400 text-sm font-sans">
                  Provide shipment parameters below to receive a formal logistics routing and cost analysis.
                </p>
              </div>

              <FormProvider formKey="request_supply_quote_termsofservice" formId="request_supply_quote_termsofservice" sectionName="acquisition-inquiry" defaultValues={{
              company_name: "",
              contact_name: "",
              email: "",
              phone: "",
              vessel_name: "",
              eta: "",
              etd: "",
              next_port_of_call: "",
              supply_type: "",
              details: "",
              preferred_delivery_date: "",
              attachment: "",
              consent: false
            }} validationRules={{
              company_name: {
                required: true
              },
              contact_name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                pattern: "phone"
              },
              vessel_name: {
                required: true
              },
              eta: {
                required: true
              },
              etd: {
                required: true
              },
              next_port_of_call: {
                required: true
              },
              supply_type: {
                required: true
              },
              details: {
                required: true
              },
              consent: {
                required: true
              }
            }} submitText="Submit Manifest & Request Quote" successMessage="Manifest received. Our logistics coordinators are calculating your routing options." errorMessage="Transmission error. Please verify your data and resubmit the manifest.">
                <InquiryForm />
              </FormProvider>
            </div>
          </div>

        </div>
      </div>
    </section>;
}
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center text-center py-12 space-y-4 animate-in fade-in zoom-in duration-300">
        <div className="size-16 bg-primary/20 text-primary rounded-none flex items-center justify-center mb-2">
          <CheckCircle2 className="size-8" />
        </div>
        <h4 className="text-2xl font-serif font-bold text-white uppercase tracking-tight">Transmission Successful</h4>
        <p className="text-slate-400 max-w-xs mx-auto font-sans">{successMessage}</p>
        <Button variant="outline" className="mt-6 border-primary/20 rounded-none hover:bg-primary/5 hover:text-primary font-mono text-xs uppercase tracking-widest" onClick={() => window.location.reload()}>
          New Quote Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="contact_name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[10px] uppercase font-mono tracking-widest text-slate-400">Contact Name *</FormLabel>
              <FormControl>
                <Input className="flex h-10 w-full rounded-none border-t-0 border-l-0 border-r-0 border-b border-slate-500 bg-transparent px-0 py-2 text-sm ring-0 focus-visible:ring-0 focus-visible:border-primary transition-all text-white placeholder:text-slate-600" placeholder="Maria Alvarez" {...field} />
              </FormControl>
              <FormMessage className="text-[10px] font-mono" />
            </FormItem>} />

        <FormField control={control} name="company_name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[10px] uppercase font-mono tracking-widest text-slate-400">Company Name *</FormLabel>
              <FormControl>
                <Input className="flex h-10 w-full rounded-none border-t-0 border-l-0 border-r-0 border-b border-slate-500 bg-transparent px-0 py-2 text-sm ring-0 focus-visible:ring-0 focus-visible:border-primary transition-all text-white placeholder:text-slate-600" placeholder="Oceanic Shipping Ltd." {...field} />
              </FormControl>
              <FormMessage className="text-[10px] font-mono" />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[10px] uppercase font-mono tracking-widest text-slate-400">Email Address *</FormLabel>
              <FormControl>
                <Input type="email" className="flex h-10 w-full rounded-none border-t-0 border-l-0 border-r-0 border-b border-slate-500 bg-transparent px-0 py-2 text-sm ring-0 focus-visible:ring-0 focus-visible:border-primary transition-all text-white placeholder:text-slate-600" placeholder="maria@oceanicshipping.com" {...field} />
              </FormControl>
              <FormMessage className="text-[10px] font-mono" />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[10px] uppercase font-mono tracking-widest text-slate-400">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" className="flex h-10 w-full rounded-none border-t-0 border-l-0 border-r-0 border-b border-slate-500 bg-transparent px-0 py-2 text-sm ring-0 focus-visible:ring-0 focus-visible:border-primary transition-all text-white placeholder:text-slate-600" placeholder="+1 555 123 4567" {...field} />
              </FormControl>
              <FormMessage className="text-[10px] font-mono" />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="vessel_name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[10px] uppercase font-mono tracking-widest text-slate-400">Vessel Name / IMO *</FormLabel>
              <FormControl>
                <Input className="flex h-10 w-full rounded-none border-t-0 border-l-0 border-r-0 border-b border-slate-500 bg-transparent px-0 py-2 text-sm ring-0 focus-visible:ring-0 focus-visible:border-primary transition-all text-white placeholder:text-slate-600" placeholder="MV Atlantic Star / 9876543" {...field} />
              </FormControl>
              <FormMessage className="text-[10px] font-mono" />
            </FormItem>} />

        <FormField control={control} name="supply_type" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[10px] uppercase font-mono tracking-widest text-slate-400">Type of Supply Needed</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="flex h-10 w-full rounded-none border-t-0 border-l-0 border-r-0 border-b border-slate-500 bg-transparent px-0 py-2 text-sm ring-0 focus:ring-0 focus:border-primary transition-all text-white">
                    <SelectValue placeholder="Select supply category" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="bg-[#0D1F3D] border-slate-800 text-white">
                  {supplyOptions.map((opt, i) => <SelectItem key={opt} value={opt} data-index={i}>
                      {opt}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormMessage className="text-[10px] font-mono" />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="eta" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[10px] uppercase font-mono tracking-widest text-slate-400 flex items-center gap-2">
                <SafeHydrate><Calendar className="size-3" /></SafeHydrate> ETA (Estimated Time of Arrival) *
              </FormLabel>
              <FormControl>
                <Input type="date" className="flex h-10 w-full rounded-none border-t-0 border-l-0 border-r-0 border-b border-slate-500 bg-transparent px-0 py-2 text-sm ring-0 focus-visible:ring-0 focus-visible:border-primary transition-all text-white placeholder:text-slate-600 uppercase" {...field} />
              </FormControl>
              <FormMessage className="text-[10px] font-mono" />
            </FormItem>} />

        <FormField control={control} name="etd" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[10px] uppercase font-mono tracking-widest text-slate-400 flex items-center gap-2">
                <SafeHydrate><Calendar className="size-3" /></SafeHydrate> ETD (Estimated Time of Departure) *
              </FormLabel>
              <FormControl>
                <Input type="date" className="flex h-10 w-full rounded-none border-t-0 border-l-0 border-r-0 border-b border-slate-500 bg-transparent px-0 py-2 text-sm ring-0 focus-visible:ring-0 focus-visible:border-primary transition-all text-white placeholder:text-slate-600 uppercase" {...field} />
              </FormControl>
              <FormMessage className="text-[10px] font-mono" />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="next_port_of_call" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[10px] uppercase font-mono tracking-widest text-slate-400 flex items-center gap-2">
                <Anchor className="size-3" /> Next Port of Call *
              </FormLabel>
              <FormControl>
                <Input className="flex h-10 w-full rounded-none border-t-0 border-l-0 border-r-0 border-b border-slate-500 bg-transparent px-0 py-2 text-sm ring-0 focus-visible:ring-0 focus-visible:border-primary transition-all text-white placeholder:text-slate-600" placeholder="Port of Cartagena, Colombia" {...field} />
              </FormControl>
              <FormMessage className="text-[10px] font-mono" />
            </FormItem>} />

        <FormField control={control} name="preferred_delivery_date" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[10px] uppercase font-mono tracking-widest text-slate-400 flex items-center gap-2">
                <SafeHydrate><Calendar className="size-3" /></SafeHydrate> Preferred Delivery Date
              </FormLabel>
              <FormControl>
                <Input type="date" className="flex h-10 w-full rounded-none border-t-0 border-l-0 border-r-0 border-b border-slate-500 bg-transparent px-0 py-2 text-sm ring-0 focus-visible:ring-0 focus-visible:border-primary transition-all text-white placeholder:text-slate-600 uppercase" {...field} />
              </FormControl>
              <FormMessage className="text-[10px] font-mono" />
            </FormItem>} />
      </div>

      <FormField control={control} name="attachment" render={({
      field: {
        value,
        onChange,
        ...fieldProps
      }
    }) => <FormItem>
            <FormLabel className="text-[10px] uppercase font-mono tracking-widest text-slate-400 flex items-center gap-2">
              <Upload className="size-3" /> Attach Specification Document (optional)
            </FormLabel>
            <FormControl>
              <Input type="file" className="flex h-10 w-full rounded-none border-t-0 border-l-0 border-r-0 border-b border-slate-500 bg-transparent px-0 py-2 text-sm ring-0 focus-visible:ring-0 focus-visible:border-primary transition-all text-white file:text-primary file:font-mono file:text-[10px] file:uppercase file:bg-primary/10 file:border-0 file:px-2 file:mr-2 cursor-pointer" onChange={e => onChange(e.target.files?.[0])} {...fieldProps} />
            </FormControl>
            <FormMessage className="text-[10px] font-mono" />
          </FormItem>} />

      <FormField control={control} name="details" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[10px] uppercase font-mono tracking-widest text-slate-400">Request details *</FormLabel>
            <FormControl>
              <Textarea className="flex min-h-[120px] w-full rounded-none border-t-0 border-l-0 border-r-0 border-b border-slate-500 bg-transparent px-0 py-2 text-sm ring-0 focus-visible:ring-0 focus-visible:border-primary transition-all text-white placeholder:text-slate-600 resize-y" placeholder="Provide a detailed description of the items, quantities, and any special handling instructions." {...field} />
            </FormControl>
            <FormDescription className="text-[10px] font-mono text-slate-500">
              The more detail you provide, the quicker we can generate an accurate quote.
            </FormDescription>
            <FormMessage className="text-[10px] font-mono" />
          </FormItem>} />

      <FormField control={control} name="consent" render={({
      field
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 p-4 border border-slate-800 bg-slate-900/40">
            <FormControl>
              <input type="checkbox" checked={field.value} onChange={field.onChange} className="size-4 mt-1 border-slate-500 rounded-none bg-transparent accent-primary cursor-pointer" />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="text-[10px] uppercase font-mono tracking-widest text-slate-400 cursor-pointer">
                I consent to the processing of my personal data for the purpose of responding to my service inquiry, in accordance with the <Link to="/privacy-policy" className="text-primary hover:underline">Privacy Policy</Link>.
              </FormLabel>
              <FormMessage className="text-[10px] font-mono" />
            </div>
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-none flex items-start gap-2 text-destructive text-[10px] font-mono uppercase">
          <AlertCircle className="size-4 mt-0.5 shrink-0" />
          <p>{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-primary hover:bg-[#B08D46] text-[#0A192F] font-serif font-bold uppercase rounded-none shadow-none transition-all duration-300 h-12">
        {isSubmitting ? <span className="flex items-center gap-2">
            <span className="size-4 border-2 border-[#0A192F]/30 border-t-[#0A192F] rounded-full animate-spin" />
            Transmitting...
          </span> : <span className="flex items-center gap-2 tracking-widest">
            Submit Manifest & Request Quote <ArrowRight className="size-4" />
          </span>}
      </Button>
    </Form>;
}